import React, { memo } from "react";
import { Box, type BoxProps } from "@chakra-ui/react";

export type PublicCardTone =
  | "neutral"
  | "primary"
  | "secondary"
  | "tertiary"
  | "success"
  | "warning"
  | "danger";

export type PublicCardVariant =
  | "flat"
  | "outlined"
  | "tinted"
  | "gradient"
  | "feature"
  | "task";

export type PublicCardProps = Omit<BoxProps, "children"> & {
  children: React.ReactNode;
  tone?: PublicCardTone;
  variant?: PublicCardVariant;
  interactive?: boolean;
};

const PUBLIC_CARD_TONES = {
  neutral: {
    accent: "moveon.publicFont",
    border: "moveon.public.surface.border",
    frameCss: "var(--chakra-colors-moveon-public-surface-border, #e5e7eb)",
  },
  primary: {
    accent: "moveon.primary",
    border: "moveon.primary",
    frameCss: "var(--chakra-colors-moveon-primary, #2563eb)",
  },
  secondary: {
    accent: "moveon.secondary",
    border: "moveon.secondary",
    frameCss: "var(--chakra-colors-moveon-secondary, #7e22ce)",
  },
  tertiary: {
    accent: "moveon.tertiary",
    border: "moveon.tertiary",
    frameCss: "var(--chakra-colors-moveon-tertiary, #4f46e5)",
  },
  success: {
    accent: "moveon.success",
    border: "moveon.success",
    frameCss: "var(--chakra-colors-moveon-success, #22c55e)",
  },
  warning: {
    accent: "moveon.warning",
    border: "moveon.warning",
    frameCss: "var(--chakra-colors-moveon-warning, #d97706)",
  },
  danger: {
    accent: "moveon.danger",
    border: "moveon.danger",
    frameCss: "var(--chakra-colors-moveon-danger, #dc2626)",
  },
} as const;

const PUBLIC_CARD_VARIANTS = {
  flat: {
    bg: "moveon.public.surface.base",
    borderColor: "moveon.public.surface.border",
    boxShadow: "none",
  },
  outlined: {
    bg: "moveon.public.surface.base",
    borderColor: "moveon.public.surface.border",
    boxShadow: "sm",
  },
  tinted: {
    bg: "moveon.public.surface.subtle",
    borderColor: "moveon.public.surface.border",
    boxShadow: "none",
  },
  gradient: {
    boxShadow: "sm",
  },
  feature: {
    bg: "moveon.public.surface.base",
    borderColor: "moveon.public.surface.border",
    boxShadow: "sm",
  },
  task: {
    bg: "moveon.public.surface.base",
    borderColor: "moveon.public.surface.border",
    boxShadow: "md",
  },
} as const;

const PUBLIC_CARD_FOCUS_PROPS = {
  outlineWidth: "2",
  outlineStyle: "solid",
  outlineColor: "moveon.primary",
  outlineOffset: "2px",
} as const;

function PublicCard({
  children,
  tone = "neutral",
  variant = "outlined",
  interactive = false,
  ...boxProps
}: PublicCardProps) {
  const toneProps = PUBLIC_CARD_TONES[tone];
  const variantProps = PUBLIC_CARD_VARIANTS[variant];
  const gradientProps =
    variant === "gradient"
      ? {
          bg: "moveon.public.surface.base",
          backgroundImage: [
            "linear-gradient(to top,",
            `color-mix(in srgb, ${toneProps.frameCss} 22%,`,
            "var(--chakra-colors-white, #ffffff)),",
            "var(--chakra-colors-white, #ffffff))",
          ].join(" "),
        }
      : {};
  const toneBorderProps =
    variant === "feature" || variant === "task" || variant === "gradient"
      ? { borderColor: toneProps.border }
      : {};
  const interactiveProps = interactive
    ? {
        cursor: "pointer",
        transitionDuration: "normal",
        transitionProperty: "backgrounds, border-color, box-shadow, transform",
        _hover: {
          borderColor: toneProps.accent,
          boxShadow: "md",
          transform: "translateY(-1px)",
        },
        _focusVisible: PUBLIC_CARD_FOCUS_PROPS,
      }
    : {};

  return (
    <Box
      rounded="md"
      borderWidth="1"
      borderStyle="solid"
      p={{ base: "4", md: "5" }}
      color="moveon.publicFont"
      overflowWrap="anywhere"
      {...variantProps}
      {...gradientProps}
      {...toneBorderProps}
      {...interactiveProps}
      {...boxProps}
    >
      {children}
    </Box>
  );
}

export default memo(PublicCard);
